import React, { useMemo } from "react";
import DelegationForm from "../DelegationForm/DelegationForm";
import * as pedestrianRightService from "../../services/pedestrianRights.service";
import * as pedestrianRightsActions from "../../store/actions/pedestrianRights.actions";
import { ParkingRightsRequestModel } from "../../models/ParkingRightsRequestModel";
import { DelegationFormDetails, DelegeeWithPmcs } from "../DelegationForm/DelegationFormDetails";
import { InvitePedestrianRightRequestModel } from "../../models/InvitePedestrianRightRequestModel";
import { ParkingProductModel, ParkingProductType } from "../../models/ParkingProductModel";
import { PedestrianProductModel } from "../../models/PedestrianProductModel";
import { DataType } from "../../store/reducers/parking-products";
import { PedestrianRightsState } from "../../store/reducers/pedestrian-rights.reducer";
import {
    PedestrianDelegeeDetails,
    UpdatePedestrianRightDelegeeModel,
} from "../../api/pedestrianRights.api";
import { useAppDispatch, useAppSelector } from "../../store";
import { isNil } from "lodash";
import useFleetManagerContext from "../../context/hooks/useFleetManagerContext";
import { createSelector } from "@reduxjs/toolkit";
import { RootReducer } from "../../store/reducers";
import { useAppContext } from "../../context/hooks";

interface IPedestrianProps {
    onClose: () => void;
}

function mapToParkingProduct(pedestrianProduct: PedestrianProductModel): ParkingProductModel {
    return {
        id: pedestrianProduct.seasonTicketId,
        pmcId: pedestrianProduct.pmcId,
        pmcName: pedestrianProduct.pmcName,
        location: pedestrianProduct.locationName,
        totalDelegatedPedestrianRights: pedestrianProduct.totalDelegatedPedestrianRights,
        totalPedestrianRights: pedestrianProduct.totalPedestrianRights,
        totalParkingRights: 0,
        totalDelegatedParkingRights: 0,
        totalAssignedParkingRights: 0,
        hasUnlimitedEntryRights: false,
        productType: ParkingProductType.PedestrianAccess,
    };
}

function createInviteModel(
    parkingRights: ParkingRightsRequestModel,
    pedestrianRights: PedestrianRightsState
): InvitePedestrianRightRequestModel {
    return {
        customerId: parkingRights.seasonTicketOwnerCrmId,
        name: parkingRights.name,
        email: parkingRights.email,
        pedestrianProducts: parkingRights.pmcIds.map((pmcId) => ({
            pmcId: pmcId,
            seasonTicketReferenceId: pedestrianRights.pedestrianProducts.data!.find(
                (x) => x.pmcId === pmcId
            )?.seasonTicketReferenceId!,
        })),
    };
}

function createUpdateModel(
    customerId: string,
    parkingRights: ParkingRightsRequestModel,
    delegeeDetails: PedestrianDelegeeDetails
): UpdatePedestrianRightDelegeeModel {
    return {
        customerId,
        name: parkingRights.name || "",
        pmcIds: parkingRights.pmcIds,
        delegeeId: delegeeDetails.id,
    };
}

function mapToDelegeeWithPmcs(stateDelegee: PedestrianDelegeeDetails): DelegeeWithPmcs {
    return {
        id: stateDelegee.id,
        emailAddress: stateDelegee.delegeeEmail,
        name: stateDelegee.delegeeName,
        pmcIds: stateDelegee.pmcIds,
    };
}

const selectParkingProducts = createSelector(
    (state: RootReducer) => state.pedestrianRights.pedestrianProducts,
    ({ loading, data, error }): DataType<ParkingProductModel[]> => {
        const parkingProducts = data?.map(mapToParkingProduct);
        return {
            loading,
            data: parkingProducts,
            error,
        };
    }
);

const selectDelegeeWithPmcs = createSelector(
    (state: RootReducer) => state.pedestrianRights.delegeeDetails,
    (stateDelegee): DataType<DelegeeWithPmcs> => {
        return {
            loading: stateDelegee.loading,
            data: isNil(stateDelegee.data) ? null : mapToDelegeeWithPmcs(stateDelegee.data),
            error: stateDelegee.error,
        };
    }
);

function selectError(state: RootReducer) {
    const err = state.pedestrianRights.updateState.error || state.pedestrianRights.error;
    if (!err) {
        return null;
    }
    return err;
}

function selectLoading(state: RootReducer): boolean {
    return state.pedestrianRights.loading || state.pedestrianRights.updateState.loading;
}

const selectDelegationFormDetails = createSelector(
    // these deps end up in useEffect dep array inside ParkingRightsGrid, so I need to memoize them to not trigger that useEffect...
    // Also better performance maybe
    [selectParkingProducts, selectDelegeeWithPmcs, selectError, selectLoading],
    (parkingProducts, delegeeDetails, error, loading): DelegationFormDetails => {
        return {
            delegeeDetails,
            parkingProducts,
            error: error,
            isBulkImportCompleted: true,
            loading,
        };
    }
);

const PedestrianForm: React.FC<IPedestrianProps> = (props) => {
    const dispatch = useAppDispatch();
    const pedestrianRights = useAppSelector((s) => s.pedestrianRights);
    const { fleetManagerState } = useFleetManagerContext();
    const {
        appState: {
            user: { seasonTicketOwnerCrmId: customerId },
        },
    } = useAppContext();
    const inviteDelegee = async (
        parkingRights: ParkingRightsRequestModel,
        callback?: () => void
    ) => {
        const request = createInviteModel(parkingRights, pedestrianRights);
        await dispatch(pedestrianRightsActions.inviteActionThunk(request)).unwrap();
        callback?.();
    };

    const updateDelegee = async (
        parkingRights: ParkingRightsRequestModel,
        callback?: () => void
    ) => {
        const request = createUpdateModel(
            customerId!,
            parkingRights,
            pedestrianRights.delegeeDetails.data!
        );
        await dispatch(
            pedestrianRightsActions.updatePedestrianDelegeeActionThunk(request)
        ).unwrap();
        callback?.();
    };

    const formDetails = useAppSelector(selectDelegationFormDetails);

    return (
        <div>
            <DelegationForm
                onClose={props.onClose}
                refreshHandler={() => {}}
                getProducts={(crmId, language) =>
                    dispatch(pedestrianRightsActions.getPedestrianProductsThunk({ seasonTicketOwnerCrmId: crmId, language }))
                }
                inviteDelegee={inviteDelegee}
                updateDelegation={updateDelegee}
                getDelegeeDetails={(id) =>
                    dispatch(pedestrianRightsActions.getPedestrianDelegeeDetailsActionThunk(id))
                }
                clearDelegeeDetails={() =>
                    dispatch(pedestrianRightsActions.clearDelegeeDetailsAction())
                }
                onDelete={() => dispatch(pedestrianRightsActions.toggleShowDeletePedestrianDelegeesAction())}
                resetBulkImportProgress={() => {}}
                getBulkImportStatus={() => {}}
                disposeError={() => dispatch(pedestrianRightsActions.disposeErrorAction())}
                isEditMode={fleetManagerState.isEditDelegeeMode}
                translationContextName="pedestrianForm"
                disableMultipleOption={true}
                delegationFormDetails={formDetails}
            />
        </div>
    );
};

export default PedestrianForm;
